import FormBuilderInput from "@/components/form-builder/FormBuilderInput.vue"
import FormBuilderUploadInput from "@/components/form-builder/FormBuilderUploadInput.vue"
import GroupedToggle from "@/components/form/GroupedToggle.vue"
import TextArray from "@/components/form/TextArray.vue"
import VueMultiselect from "@/components/form/VueMultiselect.vue"
import QuillEditor from "@/components/quill/QuillEditor.vue"
import { createMultiStepPlugin } from "@formkit/addons"
import { de, en } from "@formkit/i18n"
import { generateClasses } from "@formkit/themes"
import type { FormKitValidationRule } from "@formkit/validation"
import { createInput, defaultConfig as formkitDefaultConfig } from "@formkit/vue"
import type { Composer } from "vue-i18n"
import TranslationsForm from "./components/form/TranslationsForm.vue"

const maxFileSize: FormKitValidationRule = (node, sizeInMb) => {
    const max = parseFloat(sizeInMb) * 1024 * 1024
    const value = node.value as File[] | undefined
    if (!value?.length || Number.isNaN(max)) {
        return true
    }
    return value.every(file => file.size <= max)
}

const checkboxSchemaPatch = {
    fieldset: { $el: null },
    legend: { $el: "label", attrs: { class: "col-form-label" } },
    inner: { $el: null },
}
const checkboxGroupOuterClass = "checkbox flex-grow-1 d-sm-flex"
const checkboxOuterClass = "checkbox"

export default (i18n: Composer<unknown, unknown, unknown>) =>
    formkitDefaultConfig({
        locales: {
            de: {
                ...de,
                validation: {
                    ...de.validation,
                    max_file_size: ({ args }: { args: string[] }) => `Die Datei darf maximal ${args[0]} MB groß sein.`,
                },
            },
            en: {
                ...en,
                validation: {
                    ...en.validation,
                    max_file_size: ({ args }: { args: string[] }) => `The file must be no larger than ${args[0]} MB.`,
                },
            },
        },
        locale: i18n.locale.value || "de",
        rules: { max_file_size: maxFileSize },
        config: {
            classes: generateClasses({
                "global": {
                    input: "$reset form-control",
                    outer: "$reset form-group flex-column",
                    wrapper: "$reset flex-grow-1 d-sm-flex",
                    label: "$reset col-form-label",
                    inner: "$reset flex-grow-1",
                    message: "$reset alert alert-danger",
                    decorator: "$reset form-toggler",
                },
                "multi-step": {
                    outer: "$reset",
                    wrapper: "$reset",
                    tabs: "wizard-steps-bar my-3",
                    //tabs: "$reset nav nav-tabs mb-4",
                    tab: "wizard-step multi-step-tab d-flex justify-content-center align-items-center",
                    //tab: "$reset nav-item nav-link multi-step-tab d-flex justify-content-center align-items-center",
                    badge: "$reset badge badge-danger ml-2 p-2",
                    stepNext: "$reset btn btn-primary",
                    stepPrevious: "$reset btn btn-primary",
                    stepActions: "$reset",
                },
                "submit": {
                    outer: "$reset",
                    wrapper: "$reset",
                    input: "$reset btn btn-primary",
                },
            }),
        },
        plugins: [
            createMultiStepPlugin(),
            // translation
            node => {
                if (node.props.__FormKitIsTranslated) {
                    return
                }

                if (node.props.label) {
                    node.props.label = i18n.t(node.props.label)
                    node.props.__FormKitIsTranslated = true
                }

                if (node.props.help) {
                    node.props.help = i18n.t(node.props.help)
                    node.props.__FormKitIsTranslated = true
                }

                if (node.props.options) {
                    const translateOptions = (opts: { value: string; label: string; additionalData?: any }[]) =>
                        Array.isArray(opts) ? opts.map(opt => ({ ...opt, label: i18n.t(opt.label) })) : []

                    node.props.options = translateOptions(node.props.options)
                    node.props = new Proxy(node.props, {
                        set(target, key, value) {
                            if (key === "options") {
                                // FormKit re-sets options to undefined after creation; keep the translated ones
                                if (!Array.isArray(value)) {
                                    return true
                                }
                                Object.assign(target, { options: translateOptions(value) })
                                return true
                            }

                            Object.assign(target, { [key]: value })
                            return true
                        },
                    })
                }
            },
            // checkbox layout + schema
            node => {
                node.on("created", ({ payload: node }) => {
                    if (node.props?.type !== "checkbox") return

                    // Set outer class per-node — attrs.class in schema extensions is overridden by
                    // generateClasses "$reset", so we must set it directly on the context instead.
                    const isGroup = node.props.multiple || node.props.options?.length > 0
                    const isIndented = node.props.attrs?.indented === true || node.props.attrs?.indented === "true"
                    if (node.context) {
                        node.context.classes.outer =
                            isGroup && isIndented ? checkboxGroupOuterClass : checkboxOuterClass
                        node.context.classes.inner = ""
                        if (isGroup && isIndented) {
                            node.context.classes.label = ""
                            node.context.classes.wrapper = ""
                        }
                    }

                    // Patch fieldset/legend via definition.schema wrapping (attrs.class works here
                    // since there is no generateClasses entry for these sections).
                    const definition = { ...node.props.definition }
                    const schema = definition.schema
                    definition.schema = function (extensions = {}) {
                        return schema({ ...extensions, ...checkboxSchemaPatch })
                    }
                    node.props.definition = definition
                })
            },
            // required = bold label
            node => {
                if (node.props.parsedRules?.find((rule: any) => rule.name === "required") && node.context) {
                    node.context.classes.label = "col-form-label required"
                }
            },
        ],
        inputs: {
            // checkbox: createInput(CheckboxToggle),
            translations: createInput(TranslationsForm, { type: "list" }),
            quill: createInput(QuillEditor, { type: "input" }),
            vue_multiselect: createInput(VueMultiselect, {
                type: "input",
                props: ["options", "optionsFiltered", "multiple", "groupedOptions", "groupSelect", "closeOnSelect"],
            }),
            grouped_toggle: createInput(GroupedToggle, {
                type: "input",
                props: ["options", "optionsFiltered"],
            }),
            text_array: createInput(TextArray, { type: "input" }),
            form_builder: createInput(FormBuilderInput, { type: "input" }),
            upload: createInput(FormBuilderUploadInput, { type: "input", props: ["accept", "maxFiles"] }),
        },
    })
